import React, { useEffect, useRef, useState } from 'react';
import { hapticError } from '../../../../_utils/haptics';
import { Chips, Choices, Note, Saved, Skeleton } from '../ui';
import {
  loadClientSharing, loadDiarySharing, reasonOf, saveClientShare, saveDiaryVisibility,
  useAlive, useFlash, useSerial,
} from '../data/stylist';

/**
 * A self-employed stylist's two privacy choices, shared by the setup chapter
 * (chapters/StylistPrivacy, Ben's) and Settings › Your choices, so both show
 * the same switches, the same words and save the same way:
 *
 *   clients   whether the salon can see their clients (their own direction
 *             of client sharing; the salon's direction is shown, not changed)
 *   diary     who on the team can see their diary, and so book into it
 *
 * Each tap saves straight away. The diary choice is read back before it
 * counts, because its endpoint answers yes to anything.
 *
 * The hooks hold the state and the writes; the components only draw.
 */

/* ---------------- words (Ben's, unchanged) ---------------- */

export const clientShareOptions = ({ salon, Salon }) => [
  // isShared only ever promotes: switching this off keeps new
  // clients private, but clients already shared stay shared.
  { value: 0, label: 'Just me', hint: 'New clients stay private to you. Clients already shared with the salon stay shared' },
  { value: 1, label: `Me and ${salon}`, hint: `${Salon} can see your clients and book them in when you’re away` },
];

export const diaryOptions = ({ salon, ownerName }) => [
  { value: 'everyone', label: 'The whole team', hint: `Anyone at ${salon} can see it and book you in` },
  { value: 'selected', label: 'People I choose', hint: 'Only the people you pick' },
  { value: 'nobody', label: 'Just me', hint: `Hidden from the team. ${ownerName} can still see it` },
];

/** The salon's own direction of client sharing: shown, never changed here. */
export function salonAccessLine(access, { salon, Salon, ownerFirst }) {
  if (access === null || access === undefined) return '';
  const whose = ownerFirst ? `${ownerFirst}’s` : 'the salon’s';
  return access === 1
    ? `You can also see and book ${salon}’s own clients. That’s ${whose} choice.`
    : `${Salon}’s own clients aren’t shared with you. That’s ${whose} choice.`;
}

/* ---------------- their clients ---------------- */

/**
 * clients: undefined while loading, null when it can't be told, else
 * { clientShare: 0|1, salonClientAccess: 0|1|null }.
 * `load` defaults to Ben's loader; `onSaved(v)` runs after each save lands.
 */
export function useClientShare({ ids, user, load, onSaved } = {}) {
  const alive = useAlive();
  const serial = useSerial();
  const [flashed, flash] = useFlash();
  const [clients, setClients] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;

  useEffect(() => {
    let off = false;
    Promise.resolve(load ? load() : loadClientSharing(ids, user))
      .then((c) => { if (!off) setClients(c === undefined ? null : c); })
      .catch(() => { if (!off) setClients(null); });
    return () => { off = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v) => {
    if (saving || clients === undefined) return;
    const before = clients;
    setClients((c) => ({ ...(c || { salonClientAccess: null }), clientShare: v }));
    setError('');
    setSaving(true);
    serial('share', () => saveClientShare(v === 1))
      .then(() => {
        if (!alive.current) return;
        flash('share');
        if (savedRef.current) savedRef.current(v);
      })
      .catch((e) => {
        if (!alive.current) return;
        setClients(before);
        setError(reasonOf(e, 'That didn’t save. Try again.'));
        hapticError();
      })
      .finally(() => { if (alive.current) setSaving(false); });
  };

  return { clients, saving, error, saved: !!flashed.share, pick };
}

// salonTitle: the salon's name at the start of a sentence ("The salon").
export function ClientShareChoice({ share, salon, salonTitle, ownerFirst, showAccess = true, columns = 1 }) {
  const Salon = salonTitle || salon;
  const { clients, saving, error, saved, pick } = share;
  if (clients === undefined) {
    return <div className="su-stack" aria-busy="true"><Skeleton h={72} style={{ borderRadius: 16 }} /><Skeleton h={72} style={{ borderRadius: 16 }} /></div>;
  }
  const access = clients ? clients.salonClientAccess : null;
  return (
    <>
      <Choices
        columns={columns}
        label="Who can see your clients"
        value={clients ? clients.clientShare : null}
        onChange={pick}
        options={clientShareOptions({ salon, Salon })}
      />
      <div className="su-stylist-summary" aria-live="polite">
        {saving ? <span className="su-dots" aria-label="Saving"><i /><i /><i /></span> : saved ? <Saved /> : null}
      </div>
      {error && <div className="su-error" role="alert">{error}</div>}
      {showAccess && access !== null && access !== undefined && (
        <Note tone="plain">{salonAccessLine(access, { salon, Salon, ownerFirst })}</Note>
      )}
    </>
  );
}

/* ---------------- their diary ---------------- */

/**
 * Their row on the diary-sharing list, and the writes. A few quick taps on
 * people go as one write; `settle()` sends anything held and resolves true
 * once the last write saved (false if it didn't, null if nothing was saving).
 */
export function useDiaryChoice({ ids, onSaved } = {}) {
  const alive = useAlive();
  const serial = useSerial();
  const [flashed, flash] = useFlash();
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;

  const [diary, setDiary] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let off = false;
    setLoadErr('');
    loadDiarySharing(ids)
      .then((d) => { if (!off) setDiary(d); })
      .catch((e) => { if (!off) setLoadErr(reasonOf(e, 'I couldn’t load your diary settings just now.')); });
    return () => { off = true; };
  }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const peopleTimer = useRef(null);
  // People picked a moment ago and not sent yet (a few quick taps go as one
  // write), with the choice to go back to if that write fails.
  const pending = useRef(null);
  // The latest diary write until it lands. It resolves true once it saved.
  const lastWrite = useRef(null);
  const mine = diary && diary.mine;
  const mode = mine ? (mine.diaryVisibility || '') : '';
  const chosen = ((mine && mine.diaryVisibleTo) || []).map(String);

  const writeDiary = (nextMode, nextPeople, before = mine) => {
    if (!mine) return;
    const people = nextMode === 'selected' ? nextPeople : [];
    if (alive.current) {
      setDiary((d) => ({ ...d, mine: { ...d.mine, diaryVisibility: nextMode, diaryVisibleTo: people } }));
      setSaveErr('');
      setBusy(true);
    }
    const write = serial('diary', () => saveDiaryVisibility(mine.hairdresserId, nextMode, people))
      .then((row) => {
        if (alive.current) {
          setDiary((d) => ({ ...d, mine: { ...d.mine, ...row } }));
          flash('diary');
          if (savedRef.current) savedRef.current(nextMode);
        }
        return true;
      })
      .catch((e) => {
        if (alive.current) {
          setDiary((d) => ({ ...d, mine: before }));
          setSaveErr(reasonOf(e, 'That didn’t save. Try again.'));
          hapticError();
        }
        return false;
      })
      .finally(() => {
        if (lastWrite.current === write) lastWrite.current = null;
        if (alive.current && !pending.current && !lastWrite.current) setBusy(false);
      });
    lastWrite.current = write;
  };

  // The people picked a moment ago, sent now.
  const sendPeople = () => {
    clearTimeout(peopleTimer.current);
    const held = pending.current;
    pending.current = null;
    if (held) writeDiary('selected', held.list, held.before);
  };
  const sendRef = useRef(sendPeople);
  sendRef.current = sendPeople;
  // Leaving within a moment of a pick still sends it.
  useEffect(() => () => sendRef.current(), []);

  const pickMode = (v) => {
    if (v === mode) return;
    const held = pending.current;
    clearTimeout(peopleTimer.current);
    pending.current = null;
    writeDiary(v, v === 'selected' ? chosen : [], held ? held.before : mine);
  };

  // Busy from the first tap, so whatever waits on it waits for the write.
  const pickPeople = (list) => {
    pending.current = { list, before: pending.current ? pending.current.before : mine };
    setDiary((d) => ({ ...d, mine: { ...d.mine, diaryVisibleTo: list } }));
    setBusy(true);
    clearTimeout(peopleTimer.current);
    peopleTimer.current = setTimeout(() => sendRef.current(), 500);
  };

  const settle = () => {
    sendPeople();
    return lastWrite.current || null;
  };

  return {
    diary, loadErr, retry: () => setAttempt((n) => n + 1),
    mine, mode, chosen, busy, saveErr, saved: !!flashed.diary,
    pickMode, pickPeople, settle,
  };
}

export function DiaryChoice({ choice, ids, ownerId, salon, ownerName, onRetry, missingNote }) {
  const { diary, loadErr, mine, mode, chosen, busy, saveErr, saved, pickMode, pickPeople } = choice;
  const others = ((diary && diary.staff) || [])
    .filter((s) => s && !(ids || []).includes(String(s.hairdresserId)) && String(s.hairdresserId) !== ownerId)
    .map((s) => ({ value: String(s.hairdresserId), label: s.name || 'Team member' }));

  return (
    <>
      {loadErr && !diary && (
        <div className="su-error" role="alert">
          {loadErr} <button type="button" className="su-link su-stylist-retry" onClick={onRetry}>Try again</button>
        </div>
      )}
      {!diary && !loadErr && (
        <div className="su-stack" aria-busy="true"><Skeleton h={72} style={{ borderRadius: 16 }} /><Skeleton h={72} style={{ borderRadius: 16 }} /><Skeleton h={72} style={{ borderRadius: 16 }} /></div>
      )}
      {diary && !mine && (
        <Note tone="warn">{missingNote || 'Your diary settings aren’t ready here yet. You can choose later in Settings, under Diary sharing.'}</Note>
      )}
      {mine && (
        <>
          <Choices
            columns={1}
            label="Who can see your diary"
            value={mode || null}
            onChange={pickMode}
            options={diaryOptions({ salon, ownerName })}
          />
          {mode === 'selected' && (
            <div className="su-follow">
              {others.length ? (
                <>
                  <div className="su-follow-row" style={{ marginBottom: 10 }}>
                    <div><b>Who can see it</b><small>{chosen.length ? `${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}` : 'Pick at least one person, or choose Just me'}</small></div>
                  </div>
                  <Chips options={others} value={chosen} onChange={pickPeople} label="Who can see your diary" />
                </>
              ) : (
                <div className="su-follow-row"><div><b>Nobody else here yet</b><small>When the team grows, you can pick people in Settings.</small></div></div>
              )}
            </div>
          )}
          <div className="su-stylist-summary" aria-live="polite">
            {busy ? <span className="su-dots" aria-label="Saving"><i /><i /><i /></span> : saved ? <Saved /> : null}
          </div>
          {saveErr && <div className="su-error" role="alert">{saveErr}</div>}
        </>
      )}
    </>
  );
}
